/**
 * Toasts. Every mutation in this console reports its outcome here — success and failure
 * both. A mutation that succeeds silently is a mutation an operator will run twice.
 *
 * Rules encoded below:
 *   - Errors do not auto-dismiss. A failure that vanishes before it is read is a failure
 *     that gets reported to us as "it just didn't work".
 *   - Every toast is announced to screen readers via a polite live region (assertive for
 *     failures), because the visual toast is bottom-right and the operator's eyes are on
 *     the form they just submitted.
 *   - A toast may carry one action (usually "Undo" or "Open"). More than one is a dialog.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';

export type ToastTone = 'ok' | 'danger' | 'warn' | 'info';

export type ToastInput = {
  tone?: ToastTone;
  title: string;
  body?: string;
  /** Milliseconds. 0 or omitted on `danger` means it stays until dismissed. */
  durationMs?: number;
  action?: { label: string; onClick: () => void };
};

type Toast = ToastInput & { id: number; tone: ToastTone };

export type ToastApi = {
  push: (input: ToastInput) => number;
  success: (title: string, body?: string) => number;
  error: (title: string, body?: string) => number;
  warn: (title: string, body?: string) => number;
  info: (title: string, body?: string) => number;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_MS: Record<ToastTone, number> = {
  ok: 4000,
  info: 5000,
  warn: 8000,
  danger: 0, // sticky — see header
};

const TONE_ICON: Record<ToastTone, IconName> = {
  ok: 'check',
  danger: 'alert-triangle',
  warn: 'alert-triangle',
  info: 'info',
};

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput): number => {
      const id = nextId++;
      const tone = input.tone ?? 'info';
      const toast: Toast = { ...input, id, tone };
      setToasts((list) => {
        // A wall of toasts is noise. Newest five, oldest evicted.
        const next = [...list, toast];
        return next.length > 5 ? next.slice(next.length - 5) : next;
      });
      const ms = input.durationMs ?? DEFAULT_MS[tone];
      if (ms > 0) {
        timers.current.set(
          id,
          window.setTimeout(() => dismiss(id), ms),
        );
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const handle of map.values()) window.clearTimeout(handle);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      push,
      dismiss,
      success: (title, body) => push({ tone: 'ok', title, ...(body ? { body } : {}) }),
      error: (title, body) => push({ tone: 'danger', title, ...(body ? { body } : {}) }),
      warn: (title, body) => push({ tone: 'warn', title, ...(body ? { body } : {}) }),
      info: (title, body) => push({ tone: 'info', title, ...(body ? { body } : {}) }),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="nd-toasts" role="region" aria-label="Notifications">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="nd-toast"
            data-tone={t.tone}
            role={t.tone === 'danger' ? 'alert' : 'status'}
            aria-live={t.tone === 'danger' ? 'assertive' : 'polite'}
          >
            <Icon name={TONE_ICON[t.tone]} size={15} />
            <div style={{ minWidth: 0, flex: '1 1 auto' }}>
              <div className="nd-toast-title">{t.title}</div>
              {t.body ? <div className="nd-toast-body">{t.body}</div> : null}
              {t.action ? (
                <div className="nd-toast-actions">
                  <button
                    type="button"
                    className="nd-btn"
                    data-size="sm"
                    onClick={() => {
                      t.action?.onClick();
                      dismiss(t.id);
                    }}
                  >
                    {t.action.label}
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="nd-btn nd-btn-icon"
              data-variant="ghost"
              data-size="sm"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              <Icon name="close" size={12} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Throws if used outside the provider. That is a wiring bug, and a silent no-op toast
 * would hide every failure the console is supposed to report.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast() must be used inside <ToastProvider>');
  return ctx;
}

export default ToastProvider;
